

import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { BellIcon } from './icons/BellIcon';
import { Notification } from '../types';

export const NotificationBell: React.FC = () => {
    const { notifications, markAsRead, showMissingEntriesModal, clearNotifications } = useNotifications();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const unreadCount = notifications.filter(n => !n.read).length;

    const toggleDropdown = () => setIsOpen(prev => !prev);

    const handleNotificationClick = (notification: Notification) => {
        if (notification.type === 'missing_entries' && notification.payload.items.length > 0) {
            showMissingEntriesModal(notification.payload);
        }
        markAsRead(notification.id);
        setIsOpen(false);
    };

    const handleClearAll = () => {
        clearNotifications();
        setIsOpen(false);
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const timeSince = (dateString: string) => {
        const seconds = Math.floor((new Date().getTime() - new Date(dateString).getTime()) / 1000);
        let interval = seconds / 31536000;
        if (interval > 1) return Math.floor(interval) + " anos";
        interval = seconds / 2592000;
        if (interval > 1) return Math.floor(interval) + " meses";
        interval = seconds / 86400;
        if (interval > 1) return Math.floor(interval) + " dias";
        interval = seconds / 3600;
        if (interval > 1) return Math.floor(interval) + " horas";
        interval = seconds / 60;
        if (interval > 1) return Math.floor(interval) + " minutos";
        return "agora";
    }
    
    const dropdownPositionClasses = 'right-0 mt-2';

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={toggleDropdown}
                className="relative p-2 rounded-full text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-tertiary focus:outline-none transition-colors"
                aria-label="Notificações"
            >
                <BellIcon className="text-2xl" />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 block h-4 w-4 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center ring-2 ring-white dark:ring-dark-secondary">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>
            {isOpen && (
                <div className={`absolute w-80 bg-white dark:bg-dark-secondary rounded-lg shadow-lg overflow-hidden z-20 ${dropdownPositionClasses}`}>
                    <div className="flex justify-between items-center p-3 font-bold text-gray-900 dark:text-dark-text-primary border-b border-gray-200 dark:border-dark-tertiary">
                        <span>Notificações</span>
                        {notifications.length > 0 && (
                             <button
                                onClick={handleClearAll}
                                className="text-xs font-normal text-brand-teal hover:underline focus:outline-none"
                            >
                                Limpar tudo
                            </button>
                        )}
                    </div>
                    <ul className="max-h-96 overflow-y-auto">
                        {notifications.length > 0 ? (
                            notifications.map(n => (
                                <li key={n.id} onClick={() => handleNotificationClick(n)} className={`p-3 border-b border-gray-200 dark:border-dark-tertiary hover:bg-gray-100 dark:hover:bg-dark-tertiary cursor-pointer ${!n.read ? 'bg-brand-teal/20' : ''}`}>
                                    <p className="text-sm text-gray-800 dark:text-dark-text-primary">{n.message}</p>
                                    <p className="text-xs text-gray-500 dark:text-dark-text-secondary mt-1">{timeSince(n.createdAt)} atrás</p>
                                </li>
                            ))
                        ) : (
                            <li className="p-4 text-center text-sm text-gray-500 dark:text-dark-text-secondary">Nenhuma notificação.</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};